import { useEffect, useMemo, useState } from 'react';

const API_BASE = (import.meta.env.VITE_API_URL ?? 'https://api.itpos.itrionsoft.com').replace(/\/$/, '');

// =====================================================================
// SmartMenu (Fase 12) — Carta digital pública con QR
//
// URL: /{tenantSlug}/{centerId}[/{locationId}]
// Sin auth. Cliente entra desde QR de la mesa, ve carta, añade items al
// carrito, manda pedido (dine_in/takeaway/delivery) y opcionalmente paga
// online (Scan&Pay con Stripe).
// =====================================================================

type Tenant = { name: string; slug: string };
type Center = { id: string; name: string };
type Location = { id: string; name: string } | null;
type Family = { id: string; name: string; display_order: number };
type Product = {
  id: string;
  family_id: string | null;
  name: string;
  description: string | null;
  image_path: string | null;
  price: number | null;
  tax_rate: number;
  extra: Record<string, any>;
};
type Settings = {
  theme_color: string;
  logo_path: string | null;
  hero_image_path: string | null;
  welcome_message: string | null;
  languages: string[];
  default_language: string;
  show_allergens: boolean;
  show_prices: boolean;
  allow_dine_in: boolean;
  allow_takeaway: boolean;
  allow_delivery: boolean;
  require_payment_online: boolean;
  stripe_publishable_key: string | null;
};
type Catalog = {
  tenant: Tenant;
  center: Center;
  location: Location;
  settings: Settings;
  families: Family[];
  products: Product[];
};
type CartItem = { product: Product; quantity: number; notes?: string };
type OrderType = 'dine_in' | 'takeaway' | 'delivery';

function parsePath(): { tenantSlug: string; centerId: string; locationId: string | null } | null {
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const tenantSlug = parts[0];
  const centerId = parts[1];
  const locationId = parts[2] ?? null;
  if (!tenantSlug || !centerId) return null;
  return { tenantSlug, centerId, locationId };
}

export default function App() {
  const route = parsePath();
  if (!route) return <Welcome />;
  return <Menu {...route} />;
}

function Welcome() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-slate-200 p-6 text-center">
      <div className="text-3xl font-bold mb-2">IT<span className="text-orange-400">pos</span> SmartMenu</div>
      <p className="text-slate-400 max-w-sm">
        Escanea el QR de tu mesa para acceder a la carta digital.
      </p>
    </div>
  );
}

function Menu({ tenantSlug, centerId, locationId }: { tenantSlug: string; centerId: string; locationId: string | null }) {
  const [cat, setCat] = useState<Catalog | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState<Map<string, CartItem>>(new Map());
  const [view, setView] = useState<'catalog' | 'cart' | 'checkout' | 'success'>('catalog');
  const [orderType, setOrderType] = useState<OrderType>('dine_in');
  const [activeFamily, setActiveFamily] = useState<string | null>(null);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderResult, setOrderResult] = useState<{ id: string; total: number } | null>(null);

  useEffect(() => {
    const url = locationId
      ? `${API_BASE}/menu/${tenantSlug}/${centerId}/${locationId}`
      : `${API_BASE}/menu/${tenantSlug}/${centerId}`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: Catalog) => {
        setCat(d);
        if (d.families.length && d.families[0]) setActiveFamily(d.families[0].id);
        // Default order type según los habilitados
        if (d.settings.allow_dine_in) setOrderType('dine_in');
        else if (d.settings.allow_takeaway) setOrderType('takeaway');
        else if (d.settings.allow_delivery) setOrderType('delivery');
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [tenantSlug, centerId, locationId]);

  const theme = cat?.settings.theme_color ?? '#f97316';

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300">Cargando carta…</div>;
  if (err || !cat) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-red-400">No se pudo cargar la carta: {err}</div>;

  const totalCart = Array.from(cart.values()).reduce((acc, it) => acc + (it.product.price ?? 0) * it.quantity, 0);
  const totalItems = Array.from(cart.values()).reduce((acc, it) => acc + it.quantity, 0);

  function addToCart(p: Product) {
    setCart((c) => {
      const next = new Map(c);
      const cur = next.get(p.id);
      next.set(p.id, { product: p, quantity: (cur?.quantity ?? 0) + 1, notes: cur?.notes });
      return next;
    });
  }
  function removeFromCart(p: Product) {
    setCart((c) => {
      const next = new Map(c);
      const cur = next.get(p.id);
      if (!cur) return c;
      if (cur.quantity <= 1) next.delete(p.id);
      else next.set(p.id, { ...cur, quantity: cur.quantity - 1 });
      return next;
    });
  }
  function setNotes(p: Product, notes: string) {
    setCart((c) => {
      const next = new Map(c);
      const cur = next.get(p.id);
      if (cur) next.set(p.id, { ...cur, notes });
      return next;
    });
  }

  async function submitOrder(formData: FormData) {
    setSubmittingOrder(true); setErr(null);
    try {
      const lines = Array.from(cart.values()).map((it) => ({
        product_id: it.product.id,
        quantity: it.quantity,
        notes: it.notes ?? undefined,
      }));
      const body: any = {
        order_type: orderType,
        sales_location_id: locationId,
        customer_name: formData.get('name') ?? undefined,
        customer_phone: formData.get('phone') ?? undefined,
        customer_email: formData.get('email') ?? undefined,
        customer_address: formData.get('address') ?? undefined,
        customer_notes: formData.get('notes') ?? undefined,
        lines,
      };
      const r = await fetch(`${API_BASE}/menu/${tenantSlug}/${centerId}/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error ?? `HTTP ${r.status}`);
      }
      const d = await r.json();
      setOrderResult({ id: d.order.id, total: d.order.total });
      setCart(new Map());
      setView('success');
    } catch (e: any) {
      setErr(e.message ?? 'Error al enviar el pedido');
    } finally {
      setSubmittingOrder(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col" style={{ '--theme': theme } as any}>
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shadow-sm">
        <div className="font-bold text-lg flex-1 truncate">{cat.tenant.name}</div>
        <div className="text-xs text-slate-500">
          {cat.center.name}{cat.location ? ` · ${cat.location.name}` : ''}
        </div>
        {totalItems > 0 && view === 'catalog' && (
          <button onClick={() => setView('cart')}
            className="rounded-full px-3 py-1.5 text-sm font-medium text-white"
            style={{ background: theme }}>
            🛒 {totalItems} · {totalCart.toFixed(2)}€
          </button>
        )}
      </header>

      {view === 'catalog' && (
        <>
          {cat.settings.welcome_message && (
            <div className="px-4 py-3 text-sm text-center bg-slate-100 border-b border-slate-200">
              {cat.settings.welcome_message}
            </div>
          )}

          {/* Familias scrollables horizontalmente */}
          <nav className="sticky top-[60px] z-10 bg-white border-b border-slate-200 overflow-x-auto whitespace-nowrap py-2 px-2">
            {cat.families.map((f) => (
              <button key={f.id}
                onClick={() => {
                  setActiveFamily(f.id);
                  document.getElementById(`family-${f.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`inline-block px-3 py-1.5 mx-1 rounded-full text-sm font-medium ${activeFamily === f.id ? 'text-white' : 'bg-slate-100 text-slate-700'}`}
                style={activeFamily === f.id ? { background: theme } : undefined}>
                {f.name}
              </button>
            ))}
          </nav>

          <main className="flex-1 px-4 py-3 space-y-6 pb-24">
            {cat.families.map((f) => {
              const productsInFamily = cat.products.filter(p => p.family_id === f.id);
              if (productsInFamily.length === 0) return null;
              return (
                <section key={f.id} id={`family-${f.id}`}>
                  <h2 className="text-lg font-bold mb-2">{f.name}</h2>
                  <div className="space-y-2">
                    {productsInFamily.map((p) => {
                      const inCart = cart.get(p.id);
                      return (
                        <article key={p.id} className="bg-white rounded-lg p-3 border border-slate-200 flex gap-3 items-start">
                          {p.image_path && (
                            <img src={p.image_path} alt="" className="w-16 h-16 rounded object-cover bg-slate-100" />
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-base">{p.name}</h3>
                            {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                            {cat.settings.show_prices && p.price !== null && (
                              <div className="text-sm font-bold mt-1" style={{ color: theme }}>{p.price.toFixed(2)}€</div>
                            )}
                          </div>
                          {inCart ? (
                            <div className="flex items-center gap-2">
                              <button onClick={() => removeFromCart(p)} className="w-8 h-8 rounded-full bg-slate-200 text-lg">−</button>
                              <span className="font-bold w-5 text-center">{inCart.quantity}</span>
                              <button onClick={() => addToCart(p)} className="w-8 h-8 rounded-full text-lg text-white" style={{ background: theme }}>+</button>
                            </div>
                          ) : (
                            <button onClick={() => addToCart(p)} disabled={p.price === null}
                              className="px-3 py-1.5 rounded-full text-sm font-medium text-white disabled:opacity-40"
                              style={{ background: theme }}>
                              Añadir
                            </button>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </main>

          {/* Botón flotante carrito */}
          {totalItems > 0 && (
            <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 shadow-lg">
              <button onClick={() => setView('cart')}
                className="w-full py-3 rounded-lg font-bold text-white"
                style={{ background: theme }}>
                Ver pedido ({totalItems}) · {totalCart.toFixed(2)}€
              </button>
            </div>
          )}
        </>
      )}

      {view === 'cart' && (
        <CartView
          cart={cart}
          theme={theme}
          settings={cat.settings}
          orderType={orderType}
          setOrderType={setOrderType}
          totalCart={totalCart}
          onAdd={addToCart}
          onRemove={removeFromCart}
          onSetNotes={setNotes}
          onBack={() => setView('catalog')}
          onContinue={() => setView('checkout')}
        />
      )}

      {view === 'checkout' && (
        <CheckoutView
          theme={theme}
          orderType={orderType}
          totalCart={totalCart}
          submitting={submittingOrder}
          err={err}
          onBack={() => setView('cart')}
          onSubmit={submitOrder}
        />
      )}

      {view === 'success' && orderResult && (
        <SuccessView
          theme={theme}
          orderId={orderResult.id}
          total={orderResult.total}
          onNew={() => { setView('catalog'); setOrderResult(null); }}
        />
      )}
    </div>
  );
}

function CartView(props: {
  cart: Map<string, CartItem>; theme: string; settings: Settings; orderType: OrderType;
  setOrderType: (t: OrderType) => void; totalCart: number;
  onAdd: (p: Product) => void; onRemove: (p: Product) => void;
  onSetNotes: (p: Product, n: string) => void; onBack: () => void; onContinue: () => void;
}) {
  const { cart, theme, settings, orderType, setOrderType, totalCart, onAdd, onRemove, onSetNotes, onBack, onContinue } = props;
  const items = Array.from(cart.values());
  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <div className="text-4xl mb-3">🛒</div>
        <p className="text-slate-500 mb-4">Tu pedido está vacío</p>
        <button onClick={onBack} className="px-6 py-3 rounded-lg text-white font-medium" style={{ background: theme }}>Ver carta</button>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="text-2xl">←</button>
        <h1 className="font-bold text-lg flex-1">Tu pedido</h1>
      </header>
      <main className="flex-1 p-4 space-y-3">
        {items.map((it) => (
          <div key={it.product.id} className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="font-medium">{it.product.name}</div>
                <div className="text-sm text-slate-500">{(it.product.price ?? 0).toFixed(2)}€ × {it.quantity}</div>
              </div>
              <div className="font-bold">{((it.product.price ?? 0) * it.quantity).toFixed(2)}€</div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => onRemove(it.product)} className="w-8 h-8 rounded-full bg-slate-200 text-lg">−</button>
              <span className="font-bold w-5 text-center">{it.quantity}</span>
              <button onClick={() => onAdd(it.product)} className="w-8 h-8 rounded-full text-lg text-white" style={{ background: theme }}>+</button>
            </div>
            <input type="text" placeholder="Notas (sin sal, poco hecho…)" defaultValue={it.notes ?? ''}
              onBlur={(e) => onSetNotes(it.product, e.target.value)}
              className="mt-2 w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
          </div>
        ))}

        <div className="bg-white rounded-lg p-3 border border-slate-200 mt-4">
          <h3 className="text-sm font-bold mb-2 text-slate-600">Tipo de pedido</h3>
          <div className="grid grid-cols-3 gap-2">
            {settings.allow_dine_in && <OrderTypeBtn current={orderType} value="dine_in" theme={theme} onClick={() => setOrderType('dine_in')} label="🍽 Mesa" />}
            {settings.allow_takeaway && <OrderTypeBtn current={orderType} value="takeaway" theme={theme} onClick={() => setOrderType('takeaway')} label="🥡 Para llevar" />}
            {settings.allow_delivery && <OrderTypeBtn current={orderType} value="delivery" theme={theme} onClick={() => setOrderType('delivery')} label="🚚 Delivery" />}
          </div>
        </div>
      </main>
      <footer className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-sm text-slate-500">Total</span>
          <span className="text-2xl font-bold">{totalCart.toFixed(2)}€</span>
        </div>
        <button onClick={onContinue} className="w-full py-3 rounded-lg font-bold text-white" style={{ background: theme }}>
          Continuar
        </button>
      </footer>
    </div>
  );
}

function OrderTypeBtn({ current, value, theme, onClick, label }: { current: OrderType; value: OrderType; theme: string; onClick: () => void; label: string }) {
  const active = current === value;
  return (
    <button onClick={onClick}
      className={`text-xs py-3 rounded-lg font-medium ${active ? 'text-white' : 'bg-slate-100 text-slate-700'}`}
      style={active ? { background: theme } : undefined}>
      {label}
    </button>
  );
}

function CheckoutView(props: {
  theme: string; orderType: OrderType; totalCart: number; submitting: boolean; err: string | null;
  onBack: () => void; onSubmit: (fd: FormData) => void;
}) {
  return (
    <div className="flex-1 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button onClick={props.onBack} className="text-2xl">←</button>
        <h1 className="font-bold text-lg flex-1">Tus datos</h1>
      </header>
      <form onSubmit={(e) => { e.preventDefault(); props.onSubmit(new FormData(e.currentTarget)); }}
        className="flex-1 p-4 space-y-3">
        <FormField label="Nombre"><input name="name" required className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></FormField>
        <FormField label="Teléfono"><input name="phone" type="tel" required className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></FormField>
        {(props.orderType === 'delivery' || props.orderType === 'takeaway') && (
          <FormField label="Email (opcional)"><input name="email" type="email" className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></FormField>
        )}
        {props.orderType === 'delivery' && (
          <FormField label="Dirección de entrega"><textarea name="address" required rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></FormField>
        )}
        <FormField label="Comentarios (opcional)"><textarea name="notes" rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></FormField>

        {props.err && <p className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{props.err}</p>}

        <div className="bg-white border border-slate-200 rounded-lg p-3 mt-4">
          <div className="flex justify-between text-sm">
            <span>Total a pagar</span>
            <span className="font-bold text-lg">{props.totalCart.toFixed(2)}€</span>
          </div>
        </div>

        <button type="submit" disabled={props.submitting}
          className="w-full py-3 rounded-lg font-bold text-white disabled:opacity-40 mt-4"
          style={{ background: props.theme }}>
          {props.submitting ? 'Enviando…' : 'Enviar pedido'}
        </button>
      </form>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

function SuccessView(props: { theme: string; orderId: string; total: number; onNew: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
      <div className="text-7xl mb-4">✅</div>
      <h1 className="text-2xl font-bold mb-2">¡Pedido recibido!</h1>
      <p className="text-slate-500 max-w-xs mb-6">
        Tu pedido <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{props.orderId.slice(0, 8)}</code> ha llegado a la cocina.
      </p>
      <p className="text-3xl font-bold mb-8" style={{ color: props.theme }}>{props.total.toFixed(2)}€</p>
      <button onClick={props.onNew} className="px-6 py-3 rounded-lg text-white font-medium" style={{ background: props.theme }}>
        Hacer otro pedido
      </button>
    </div>
  );
}
